import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useConnectStore,
  useDisconnectStore,
  usePantryForms,
  usePreferences,
  useSavePreferences,
  useSetPantryForm,
  useStores,
} from '../api/planner';
import type { Preferences } from '../api/planner';
import { useAuth } from '../auth/AuthContext';
import IngredientPicker from '../components/IngredientPicker';

/* Groups first, because that is how people describe what they don't eat.
   The corpus tags fifteen kinds of fish; ruling them out one at a time was
   not something the old list could even express. Each group covers its
   species — see planner/proteins.py. */
const PROTEIN_GROUPS = ['fish', 'shellfish', 'seafood', 'poultry', 'red meat'];

const COMMON_PROTEINS = [
  'chicken', 'beef', 'pork', 'salmon', 'shrimp', 'cod',
  'tofu', 'beans', 'lamb', 'turkey', 'eggs',
];

/** The kinds a ruled-out ingredient also covers, on demand.

    This was a `title=` attribute, so the fifteen kinds of fish behind "and 15
    kinds of it" existed only for a mouse pointer — on the phone this app is
    built for, there was no way to find out what you had just ruled out. */
function Covers({ names }: { names: string[] }) {
  const [open, setOpen] = useState(false);
  if (names.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="link-button covers__toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        and {names.length} kind{names.length === 1 ? '' : 's'} of it
      </button>
      {open && (
        <ul className="covers">
          {names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Search-and-add for ingredients to never cook with.

    This existed only as a read-only line before — the API accepted the field,
    nothing in the UI ever sent it, and the card was hidden entirely when the
    list was empty, so there was no way in at all. */
function DislikedIngredients({
  prefs,
  onChange,
  saving,
}: {
  prefs: Preferences;
  onChange: (ids: number[]) => void;
  saving: boolean;
}) {
  const current = prefs.disliked_ingredient_detail;
  const currentIds = current.map((row) => row.id);

  function add(id: number) {
    if (!currentIds.includes(id)) onChange([...currentIds, id]);
  }

  return (
    <div className="card">
      <h2>Never cook with</h2>
      <p className="muted small">
        Ruled out entirely — no recipe using one will be suggested.
      </p>

      {current.length > 0 && (
        <ul className="ruled-out">
          {current.map((row) => (
            <li key={row.id}>
              <span className="ruled-out__name">{row.name}</span>
              <Covers names={row.also_covers} />
              <button
                type="button"
                className="ghost"
                aria-label={`Allow ${row.name} again`}
                disabled={saving}
                onClick={() => onChange(currentIds.filter((id) => id !== row.id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The count is the useful part: ruling out garlic is a very different
          decision from ruling out verjus rouge. */}
      <IngredientPicker
        placeholder="Search an ingredient to rule out"
        omit={currentIds}
        onPick={(option) => add(option.id)}
        meta={(option) => `${option.recipes} recipes`}
      />
    </div>
  );
}

/** "I use the jar, not the cloves."

    Turning one of these on changes the shopping list only. The recipe still
    says four cloves of garlic, because it is somebody else's recipe and the
    list is the only part of this that is about shopping. */
function PantryForms() {
  const { data: forms } = usePantryForms();
  const setForm = useSetPantryForm();

  if (!forms || forms.length === 0) return null;

  return (
    <div className="card">
      <h2>How you keep it</h2>
      <p className="muted small">
        Buy these in the form you actually keep. Only the shopping list
        changes — the recipe still calls for what it calls for.
      </p>
      <ul className="forms">
        {forms.map((form) => (
          <li key={form.id}>
            <label>
              <input
                type="checkbox"
                checked={form.preferred}
                disabled={setForm.isPending}
                onChange={(event) =>
                  setForm.mutate({ id: form.id, preferred: event.target.checked })
                }
              />
              <span>
                <strong>{form.substitute_name}</strong> instead of{' '}
                {form.ingredient_name}
                <span className="muted small">
                  {' '}
                  · 1 {form.per_unit} ≈ {Number(form.ratio_quantity)}{' '}
                  {form.ratio_unit}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Connecting a store's cart.

    Before this existed the cart button simply failed: the token it needed had
    to be written into the database by hand, and nothing in the app could do
    it. */
function Stores() {
  const { data: stores } = useStores();
  const connect = useConnectStore();
  const disconnect = useDisconnectStore();

  // The callback lands back here with the outcome in the query string. Read
  // once as the initial state rather than in an effect — the value exists
  // before the first render, so deriving it there is a render behind.
  const [outcome] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('linked')) return null;
    return params.get('linked') === '1'
      ? 'Connected.'
      : params.get('error') || 'That did not work — try connecting again.';
  });

  // Stripping it is a real side effect: without it a reload replays the
  // message for a connection that happened days ago.
  useEffect(() => {
    if (outcome) window.history.replaceState({}, '', window.location.pathname);
  }, [outcome]);

  const cartStores = (stores ?? []).filter((store) => store.supports_cart);
  if (cartStores.length === 0) return null;

  return (
    <div className="card">
      <h2>Stores</h2>
      <p className="muted small">
        Connecting a store lets this app put the shopping list in your cart
        there.
      </p>
      {outcome && <p className="muted">{outcome}</p>}
      {/* A refused authorize call used to be completely silent: the button
          reported nothing and the browser went nowhere, which reads as a dead
          button rather than a misconfigured one. */}
      {connect.isError && (
        <p className="error small">{(connect.error as Error).message}</p>
      )}
      {disconnect.isError && (
        <p className="error small">{(disconnect.error as Error).message}</p>
      )}
      <ul className="forms">
        {cartStores.map((store) => (
          <li key={store.id} className="store-link">
            <span>
              <strong>{store.name}</strong>
              <span className="muted small">
                {store.cart_linked
                  ? store.cart_linked_at
                    ? ` · connected ${new Date(store.cart_linked_at).toLocaleDateString()}`
                    : ' · connected'
                  : ' · not connected'}
              </span>
              {store.last_error && (
                <span className="muted small"> · {store.last_error}</span>
              )}
            </span>
            {store.cart_linked ? (
              <button
                type="button"
                className="btn btn--small"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate(store.id)}
              >
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--small btn--primary"
                disabled={connect.isPending}
                onClick={() => connect.mutate(store.id)}
              >
                Connect
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Prices only this household gets at Whole Foods. */
function WholeFoodsPricing({
  prefs,
  onChange,
  saving,
}: {
  prefs: Preferences;
  onChange: (change: Partial<Preferences>) => void;
  saving: boolean;
}) {
  const { data: stores } = useStores();
  if (!(stores ?? []).some((store) => store.provider === 'whole_foods')) {
    return null;
  }

  return (
    <div className="card">
      <h2>Whole Foods</h2>
      <ul className="forms">
        <li>
          <label>
            <input
              type="checkbox"
              checked={prefs.whole_foods_prime}
              disabled={saving}
              onChange={(event) =>
                onChange({ whole_foods_prime: event.target.checked })
              }
            />
            <span>
              <strong>Prime member</strong>
              <span className="muted small"> · use Prime prices</span>
            </span>
          </label>
        </li>
        <li>
          <label>
            <input
              type="checkbox"
              checked={prefs.whole_foods_employee_discount}
              disabled={saving}
              onChange={(event) =>
                onChange({ whole_foods_employee_discount: event.target.checked })
              }
            />
            <span>
              <strong>Employee discount</strong>
              <span className="muted small"> · 20% off</span>
            </span>
          </label>
        </li>
      </ul>
    </div>
  );
}

export default function Profile() {
  const { data: prefs } = usePreferences();
  const save = useSavePreferences();
  const { user, logout } = useAuth();

  const [servings, setServings] = useState(2);
  const [meals, setMeals] = useState(4);
  const [liked, setLiked] = useState<string[]>([]);
  const [disliked, setDisliked] = useState<string[]>([]);
  const [varietyWeeks, setVarietyWeeks] = useState(8);

  useEffect(() => {
    if (!prefs) return;
    setServings(prefs.servings);
    setMeals(prefs.meals_per_week);
    setLiked(prefs.liked_proteins);
    setDisliked(prefs.disliked_proteins);
    setVarietyWeeks(prefs.variety_window_weeks);
  }, [prefs]);

  function toggle(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  /** The ruled-out group already covering a protein, if any.

      Ticking "no fish" and then finding cod still tickable read as the group
      not working. It was working; the chips just never said so. This also
      makes the honest converse visible — "no fish" leaves shrimp tickable
      because shrimp is shellfish. */
  function groupsCovering(protein: string): string | null {
    const groups = prefs?.protein_groups ?? {};
    return (
      disliked.find(
        (name) => name !== protein && (groups[name] ?? []).includes(protein),
      ) ?? null
    );
  }

  function onSave() {
    save.mutate({
      servings,
      meals_per_week: meals,
      liked_proteins: liked,
      disliked_proteins: disliked,
      variety_window_weeks: varietyWeeks,
    });
  }

  return (
    <section>
      <h1>Profile</h1>

      <div className="card">
        <h2>The week</h2>
        <label className="field">
          <span>Servings per dinner</span>
          <select value={servings} onChange={(e) => setServings(Number(e.target.value))}>
            {[2, 4, 6].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Dinners per week</span>
          <select value={meals} onChange={(e) => setMeals(Number(e.target.value))}>
            {[2, 3, 4, 5, 6, 7].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="card">
        <h2>Proteins</h2>
        <p className="muted small">
          Disliked proteins are never suggested. Liked ones are nudged up.
        </p>
        <fieldset className="chips">
          <legend>Favourites</legend>
          {COMMON_PROTEINS.map((protein) => (
            <button
              key={protein}
              type="button"
              className={`chip-toggle ${liked.includes(protein) ? 'is-on' : ''}`}
              onClick={() => toggle(liked, setLiked, protein)}
            >
              {protein}
            </button>
          ))}
        </fieldset>
        <fieldset className="chips">
          <legend>Never suggest</legend>
          {PROTEIN_GROUPS.map((group) => (
            <button
              key={group}
              type="button"
              className={`chip-toggle chip-toggle--group ${
                disliked.includes(group) ? 'is-off' : ''
              }`}
              onClick={() => toggle(disliked, setDisliked, group)}
              title={`Covers every kind of ${group}`}
            >
              no {group}
            </button>
          ))}
          {COMMON_PROTEINS.map((protein) => {
            const coveredBy = groupsCovering(protein);
            return (
              <button
                key={protein}
                type="button"
                className={`chip-toggle ${
                  disliked.includes(protein) ? 'is-off' : ''
                } ${coveredBy ? 'is-covered' : ''}`}
                disabled={Boolean(coveredBy)}
                title={
                  coveredBy
                    ? `Already ruled out by “no ${coveredBy}”`
                    : undefined
                }
                onClick={() => toggle(disliked, setDisliked, protein)}
              >
                {protein}
              </button>
            );
          })}
        </fieldset>
      </div>

      <div className="card">
        <h2>Variety</h2>
        <label className="field">
          <span>Don't repeat a dinner for {varietyWeeks} weeks</span>
          <input
            type="range"
            min={1}
            max={26}
            value={varietyWeeks}
            onChange={(e) => setVarietyWeeks(Number(e.target.value))}
          />
        </label>
      </div>

      {prefs && (
        <DislikedIngredients
          prefs={prefs}
          saving={save.isPending}
          onChange={(ids) => save.mutate({ disliked_ingredients: ids })}
        />
      )}

      <PantryForms />

      {prefs && (
        <WholeFoodsPricing
          prefs={prefs}
          saving={save.isPending}
          onChange={(change) => save.mutate(change)}
        />
      )}

      <Stores />

      <button
        className="btn btn--primary btn--wide"
        onClick={onSave}
        disabled={save.isPending}
      >
        {save.isPending ? 'Saving…' : 'Save preferences'}
      </button>
      {save.isSuccess && <p className="muted">Saved.</p>}

      <div className="card">
        <h2>Recipe data</h2>
        <p className="muted">
          Recipe cards and store ingredients are matched automatically, but you can review those matches and make corrections.
        </p>
        <Link className="btn" to="/review">
          Check the extraction
        </Link>
      </div>

      <div className="card">
        <h2>Account</h2>
        <p className="muted">Signed in as {user?.username}.</p>
        <button className="btn" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </section>
  );
}
